import React, { useMemo, useState } from 'react';
import { format, subDays, addDays } from 'date-fns';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { getAuthToken } from '../../lib/authToken';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEMO_KEY = 'planning_export_pdf';

const lireMemo = () => { try { return JSON.parse(localStorage.getItem(MEMO_KEY) || 'null') || {}; } catch { return {}; } };

// Liste des AF/AFT candidates (periode + type + financeurs) avec cases a cocher
const SelectionDossiers = ({ candidats, exclus, setExclus }) => {
  const [recherche, setRecherche] = useState('');
  const visibles = candidats.filter(d => !recherche || `${d.code} ${d.intitule}`.toLowerCase().includes(recherche.toLowerCase()));
  const nbSelect = candidats.filter(d => !exclus.includes(d.id)).length;
  return (
    <div className="space-y-1.5" data-testid="pdf-selection-dossiers">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Dossiers à éditer ({nbSelect}/{candidats.length})</Label>
        <button type="button" className="text-[11px] text-indigo-600 hover:underline" data-testid="pdf-dossiers-tous"
          onClick={() => setExclus(nbSelect === candidats.length ? candidats.map(d => d.id) : [])}>
          {nbSelect === candidats.length ? 'Tout décocher' : 'Tout sélectionner'}
        </button>
      </div>
      <Input value={recherche} onChange={e => setRecherche(e.target.value)} placeholder="Rechercher un code ou un intitulé…" className="h-8 text-xs" data-testid="pdf-dossiers-recherche" />
      <div className="max-h-44 overflow-y-auto rounded-lg border divide-y">
        {visibles.length === 0 && <p className="px-2 py-2 text-xs text-slate-400 italic">Aucun dossier pour ces critères</p>}
        {visibles.map(d => {
          const checked = !exclus.includes(d.id);
          return (
            <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={checked} className="accent-indigo-600" data-testid={`pdf-dossier-${d.code}`}
                onChange={() => setExclus(checked ? [...exclus, d.id] : exclus.filter(x => x !== d.id))} />
              <span className={`shrink-0 rounded px-1 text-[9px] font-bold ${d.type_dossier === 'aft' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{d.type_dossier === 'aft' ? 'AFT' : 'AF'}</span>
              <span className="font-medium text-slate-800 whitespace-nowrap">{d.code}</span>
              <span className="truncate text-slate-500">{d.intitule}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export const PlanningExportPdfDialog = ({ open, onOpenChange, dossiers, dossiersFinanceurs, financeursDispo }) => {
  const [memo] = useState(lireMemo);
  const [type, setType] = useState(memo.type || 'tous');
  const [financeurs, setFinanceurs] = useState(memo.financeurs === undefined ? null : memo.financeurs); // null = tous
  const [dateDebut, setDateDebut] = useState(memo.date_debut || format(subDays(new Date(), 15), 'yyyy-MM-dd'));
  const [dateFin, setDateFin] = useState(memo.date_fin || format(addDays(new Date(), 120), 'yyyy-MM-dd'));
  const [affiner, setAffiner] = useState(!!memo.affiner);
  const [avecCodeAction, setAvecCodeAction] = useState(!!memo.avec_code_action);
  const [avecBdc, setAvecBdc] = useState(!!memo.avec_bdc);
  const [exclus, setExclus] = useState(memo.exclus || []);

  const finParAf = useMemo(() => dossiersFinanceurs.reduce((acc, df) => { if (df.financeur_nom) (acc[df.action_formation_id] ||= new Set()).add(df.financeur_nom); return acc; }, {}), [dossiersFinanceurs]);

  // Memes regles que le backend : periode, type AF/AFT, financeurs selectionnes
  const candidats = useMemo(() => dossiers.filter(d => {
    if (!d.date_debut || (d.date_fin && d.date_fin < dateDebut) || d.date_debut > dateFin) return false;
    const estAft = d.type_dossier === 'aft';
    if ((type === 'af' && estAft) || (type === 'aft' && !estAft)) return false;
    if (financeurs === null) return true;
    const noms = finParAf[d.id] || finParAf[d.af_mere_id || ''] || (d.financeur ? new Set([d.financeur]) : new Set());
    return noms.size === 0 ? financeurs.includes('__sans__') : [...noms].some(n => financeurs.includes(n));
  }).sort((a, b) => (a.code || '').localeCompare(b.code || '')), [dossiers, dateDebut, dateFin, type, financeurs, finParAf]);

  const handleExport = () => {
    const selection = candidats.filter(d => !exclus.includes(d.id));
    if (affiner && selection.length === 0) { toast.error('Sélectionnez au moins un dossier'); return; }
    localStorage.setItem(MEMO_KEY, JSON.stringify({ date_debut: dateDebut, date_fin: dateFin, type, financeurs, affiner, avec_code_action: avecCodeAction, avec_bdc: avecBdc, exclus, saved_at: new Date().toISOString() }));
    const params = new URLSearchParams({ date_debut: dateDebut, date_fin: dateFin, type_dossier: type, token: getAuthToken() });
    if (financeurs !== null) params.set('financeurs', financeurs.join('|'));
    if (affiner) {
      if (avecCodeAction) params.set('avec_code_action', 'true');
      if (avecBdc) params.set('avec_bdc', 'true');
      if (selection.length < candidats.length) params.set('dossier_ids', selection.map(d => d.id).join('|'));
    }
    const a = document.createElement('a');
    a.href = `${API_URL}/planning/export-pdf?${params.toString()}`;
    a.target = '_blank'; a.rel = 'noopener noreferrer';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    onOpenChange(false);
    toast.success('PDF en cours de generation...');
  };

  const toggleFinanceur = (nom) => {
    const base = financeurs === null ? [...financeursDispo, '__sans__'] : financeurs;
    const next = base.includes(nom) ? base.filter(x => x !== nom) : [...base, nom];
    setFinanceurs(next.length === financeursDispo.length + 1 ? null : next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="pdf-export-dialog">
        <DialogHeader><DialogTitle>Exporter le planning en PDF</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Format A3 paysage. Choisissez la periode et le contenu a exporter.</p>
        <div className="space-y-3 mt-2 min-w-0">
          <div className="space-y-1.5">
            <Label className="text-xs">Contenu</Label>
            <div className="grid grid-cols-3 gap-1.5" data-testid="pdf-type-dossier">
              {[['tous', 'Édition totale', 'AF + AFT'], ['af', 'AF uniquement', 'Actions de formation'], ['aft', 'AFT uniquement', 'Actions de certification']].map(([v, l, d]) => (
                <button key={v} type="button" onClick={() => setType(v)} data-testid={`pdf-type-${v}`}
                  className={`rounded-lg border px-2 py-2 text-left transition-colors ${type === v ? 'border-indigo-600 bg-indigo-50 text-indigo-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  <span className="block text-xs font-semibold">{l}</span>
                  <span className="block text-[10px] text-slate-500">{d}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Financeurs</Label>
              <button type="button" className="text-[11px] text-indigo-600 hover:underline" data-testid="pdf-financeurs-tous" onClick={() => setFinanceurs(financeurs === null ? [] : null)}>
                {financeurs === null ? 'Tout décocher' : 'Tout sélectionner'}
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto rounded-lg border divide-y" data-testid="pdf-financeurs">
              {[...financeursDispo, '__sans__'].map(nom => (
                <label key={nom} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={financeurs === null || financeurs.includes(nom)} onChange={() => toggleFinanceur(nom)} className="accent-indigo-600" data-testid={`pdf-financeur-${nom === '__sans__' ? 'sans' : nom}`} />
                  <span className={nom === '__sans__' ? 'italic text-slate-500' : 'text-slate-700'}>{nom === '__sans__' ? 'Sans financeur' : nom}</span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-slate-400">{financeurs === null ? 'Tous les financeurs' : `${financeurs.length} sélectionné(s)`}</p>
          </div>
          {memo.saved_at && (
            <p className="text-[11px] text-indigo-600 bg-indigo-50 rounded px-2 py-1" data-testid="pdf-memo-info">
              Dernières dates saisies ({format(new Date(memo.saved_at), 'dd/MM/yyyy HH:mm')}) : du {memo.date_debut?.split('-').reverse().join('/')} au {memo.date_fin?.split('-').reverse().join('/')}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label className="text-xs">Date de debut</Label><Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} data-testid="pdf-date-debut" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Date de fin</Label><Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} data-testid="pdf-date-fin" /></div>
          </div>
          <label className="flex items-start gap-2 text-xs font-medium text-indigo-700 cursor-pointer rounded-lg border border-indigo-200 bg-indigo-50/60 px-2 py-2">
            <input type="checkbox" checked={affiner} onChange={e => setAffiner(e.target.checked)} className="accent-indigo-600 mt-0.5 shrink-0" data-testid="pdf-affiner" />
            <span>Affiner la requête <span className="font-normal text-indigo-500">(code action, BDC, sélection des AF / AFT)</span></span>
          </label>
          {affiner && (
            <div className="space-y-3 rounded-lg border border-slate-200 p-3" data-testid="pdf-options-avancees">
              <div className="space-y-1.5">
                <Label className="text-xs">Informations supplémentaires (colonne Dossier)</Label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={avecCodeAction} onChange={e => setAvecCodeAction(e.target.checked)} className="accent-indigo-600" data-testid="pdf-avec-code-action" />
                  Code action du client (dossiers financeurs)
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={avecBdc} onChange={e => setAvecBdc(e.target.checked)} className="accent-indigo-600" data-testid="pdf-avec-bdc" />
                  N° de bons de commande couvrant la période
                </label>
              </div>
              <SelectionDossiers candidats={candidats} exclus={exclus} setExclus={setExclus} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleExport} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-generate-pdf">Telecharger le PDF</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
